import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { usePageTitle } from "@/components/common/AppShell";
import Modal from "@/components/common/Modal";
import CreativeForm from "@/components/creatives/CreativeForm";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import Card from "@/components/common/Card";
import ActionMenu from "@/components/common/ActionMenu";
import SearchInput from "@/components/common/SearchInput";
import RowIcon from "@/components/common/RowIcon";
import { Plus, Pencil, Trash2, Film } from "lucide-react";
import { useMemo } from "react";

export default function CreativesPage() {
  usePageTitle("Creatives");
  const { performRequest } = useApp();

  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCreative, setEditingCreative] = useState(null);

  const fetchCreatives = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await performRequest("/creatives");
      setCreatives(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load creatives", err);
      setError(err.message || "Failed to load creatives");
    } finally {
      setLoading(false);
    }
  }, [performRequest]);

  useEffect(() => {
    fetchCreatives();
  }, [fetchCreatives]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editUuid = searchParams.get("edit");
    if (!editUuid || creatives.length === 0) return;
    const target = creatives.find((c) => c.uuid === editUuid);
    if (!target) return;
    setEditingCreative(target);
    setIsModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [searchParams, creatives, setSearchParams]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return creatives;
    return creatives.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) || c.url?.toLowerCase().includes(q),
    );
  }, [creatives, search]);

  const handleCreate = () => {
    setEditingCreative(null);
    setIsModalOpen(true);
  };

  const handleEdit = (creative) => {
    setEditingCreative(creative);
    setIsModalOpen(true);
  };

  const handleDelete = async (creative) => {
    const label = creative.name || creative.url || creative.uuid;
    if (!window.confirm(`Delete creative "${label}"?`)) return;
    try {
      await performRequest(`/creatives/${creative.uuid}`, { method: "DELETE" });
      setCreatives((prev) => prev.filter((c) => c.uuid !== creative.uuid));
    } catch (err) {
      console.error("Failed to delete creative", err);
      window.alert(
        "Failed to delete creative: " + (err.message || "Unknown error"),
      );
    }
  };

  const handleSave = async (formValues) => {
    try {
      if (editingCreative) {
        const updated = await performRequest(
          `/creatives/${editingCreative.uuid}`,
          {
            method: "PUT",
            body: JSON.stringify(formValues),
          },
        );
        setCreatives((prev) =>
          prev.map((c) => (c.uuid === editingCreative.uuid ? updated : c)),
        );
      } else {
        const created = await performRequest("/creatives", {
          method: "POST",
          body: JSON.stringify(formValues),
        });
        setCreatives((prev) => [...prev, created]);
      }
      setIsModalOpen(false);
      setEditingCreative(null);
    } catch (err) {
      console.error("Failed to save creative", err);
      window.alert(
        "Failed to save creative: " + (err.message || "Unknown error"),
      );
    }
  };

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search creatives..."
        />
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
        >
          <Plus size={16} strokeWidth={2.5} />
          Create creative
        </button>
      </div>

      {error && (
        <Card className="mb-4">
          <p className="text-sm text-(--status-danger)">{error}</p>
        </Card>
      )}

      {loading ? (
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading creatives…</p>
        </Card>
      ) : (
        <DataTable>
          <thead>
            <tr className={DATA_TABLE_CLASSES.headRow}>
              <th className={DATA_TABLE_CLASSES.th}>Name / URL</th>
              <th className={DATA_TABLE_CLASSES.th}>Duration</th>
              <th className={DATA_TABLE_CLASSES.th}>Resolution</th>
              <th className={DATA_TABLE_CLASSES.th}>Created</th>
              <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="5" className={DATA_TABLE_CLASSES.emptyCell}>
                  {search
                    ? "No creatives match your search."
                    : "No creatives yet. Upload your first creative to get started."}
                </td>
              </tr>
            ) : (
              filtered.map((creative) => (
                <tr key={creative.uuid} className={DATA_TABLE_CLASSES.bodyRow}>
                  <td className={DATA_TABLE_CLASSES.td}>
                    <div className="flex items-center gap-3">
                      <RowIcon icon={Film} />
                      <div className="min-w-0">
                        {creative.name && (
                          <div className="font-semibold">{creative.name}</div>
                        )}
                        <div
                          className="max-w-[280px] truncate text-xs text-(--text-muted)"
                          title={creative.url}
                        >
                          {creative.url}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={DATA_TABLE_CLASSES.td}>
                    {creative.durationSeconds != null
                      ? `${creative.durationSeconds}s`
                      : "—"}
                  </td>
                  <td className={DATA_TABLE_CLASSES.td}>
                    {creative.width && creative.height
                      ? `${creative.width}×${creative.height}`
                      : "—"}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {creative.createdAt
                      ? new Date(creative.createdAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    <ActionMenu
                      ariaLabel={`Actions for ${creative.name || creative.uuid}`}
                      items={[
                        {
                          label: "Edit",
                          icon: Pencil,
                          onClick: () => handleEdit(creative),
                        },
                        {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          onClick: () => handleDelete(creative),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCreative(null);
        }}
        title={editingCreative ? "Edit Creative" : "Create Creative"}
        size="medium"
      >
        <CreativeForm
          key={editingCreative?.uuid ?? "new"}
          initialCreative={editingCreative}
          onSubmit={handleSave}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingCreative(null);
          }}
        />
      </Modal>
    </section>
  );
}
